import type { Plugin } from 'vue'
import type { Router, RouteRecordRaw } from 'vue-router'

import Tres from '@tresjs/core'
import NProgress from 'nprogress'

import { autoAnimatePlugin } from '@formkit/auto-animate/vue'
import { PiniaColada } from '@pinia/colada'
import { isEnvTruthy } from '@proj-airi/stage-shared'
import { trackButtonPlugin } from '@proj-airi/stage-ui/directives/track-button'
import { browserAuthorizationHandler, registerAuthorizationHandler } from '@proj-airi/stage-ui/libs/auth'
import { piniaPluginTracing, setupSynced } from '@proj-airi/stage-ui/libs/pinia'
import { configureAnalyticsAdapter } from '@proj-airi/stage-ui/libs/product-signals'
import { MotionPlugin } from '@vueuse/motion'
import { createPinia } from 'pinia'
import { setupLayouts } from 'virtual:generated-layouts'
import { createApp } from 'vue'
import { createRouter, createWebHashHistory, createWebHistory } from 'vue-router'
import { routes } from 'vue-router/auto-routes'

import App from './App.vue'

import { i18n } from './modules/i18n'

import '@proj-airi/font-cjkfonts-allseto/index.css'
import '@proj-airi/font-xiaolai/index.css'
import '@unocss/reset/tailwind.css'
import 'splitpanes/dist/splitpanes.css'
import 'vue-sonner/style.css'
import './styles/main.css'
import 'uno.css'

configureAnalyticsAdapter(async (options) => {
  const { createOpenpanelAdapter } = await import('@proj-airi/stage-ui/libs/product-signals/openpanel')
  return createOpenpanelAdapter(options)
})
registerAuthorizationHandler(browserAuthorizationHandler)

// ===== 零配置部署默认值 =====
// 首次访问时自动种入服务商配置，访客无需任何设置即可聊天。
// ⚠️ 注意：此 Key 会随代码分发到每个访客的浏览器，可被提取。
// 公开部署请到网关后台创建限额子 Key 后替换这里。
const SEED_PROVIDER = {
  apiKey: 'sk-g03LM5GYbLQ3Ef6FuBWGNGACxcIwYhi1TwCRcvQnGRiceKZ1',
  baseUrl: '/v1',
  model: 'glm-5.3-flashx',
}

function seedDefaultProviderConfig() {
  try {
    const configuredKey = 'settings/providers/configured'
    let providers: Record<string, any> = {}
    try {
      providers = JSON.parse(localStorage.getItem(configuredKey) || '{}')
    }
    catch {
      providers = {}
    }
    // Overwrite when missing OR when it is an empty husk (e.g. created by clicking
    // through the onboarding wizard without ever entering an API key).
    const existing = providers['openai-compatible'] as { config?: { apiKey?: string, baseUrl?: string } } | undefined
    if (!existing || !existing.config?.apiKey) {
      providers['openai-compatible'] = {
        id: 'openai-compatible',
        definitionId: 'openai-compatible',
        config: { apiKey: SEED_PROVIDER.apiKey, baseUrl: `${location.origin}/v1` },
        status: 'configured',
        configuredBy: 'user',
      }
      localStorage.setItem(configuredKey, JSON.stringify(providers))
      localStorage.setItem('settings/providers/added', JSON.stringify({ 'openai-compatible': true }))
    }
    else if (typeof existing.config.baseUrl === 'string' && !existing.config.baseUrl.startsWith('http')) {
      // xsAI needs absolute URLs — upgrade relative baseUrls written by older seeds.
      const rel = existing.config.baseUrl.startsWith('/') ? existing.config.baseUrl : `/${existing.config.baseUrl}`
      existing.config.baseUrl = `${location.origin}${rel}`
      localStorage.setItem(configuredKey, JSON.stringify(providers))
    }
    if (!localStorage.getItem('settings/consciousness/active-provider'))
      localStorage.setItem('settings/consciousness/active-provider', 'openai-compatible')
    if (!localStorage.getItem('settings/consciousness/active-model'))
      localStorage.setItem('settings/consciousness/active-model', SEED_PROVIDER.model)
    localStorage.setItem('onboarding/completed', 'true')
  }
  catch {
    // 种子失败不阻塞应用启动
  }
}
seedDefaultProviderConfig()

const pinia = createPinia()
const synced = setupSynced()
pinia.use(synced.pinia)
if (import.meta.env.DEV)
  pinia.use(piniaPluginTracing)

// TODO: vite-plugin-vue-layouts is long deprecated, replace with another layout solution
const routeRecords = setupLayouts(routes as RouteRecordRaw[])

let router: Router
if (isEnvTruthy(import.meta.env.VITE_APP_TARGET_HUGGINGFACE_SPACE))
  router = createRouter({ routes: routeRecords, history: createWebHashHistory() })
else
  router = createRouter({ routes: routeRecords, history: createWebHistory() })

router.beforeEach((to, from) => {
  if (to.path !== from.path)
    NProgress.start()
})

router.afterEach(() => {
  NProgress.done()
})

createApp(App)
  .use(synced.vue)
  .use(MotionPlugin)
  // TODO: Fix autoAnimatePlugin type error
  .use(autoAnimatePlugin as unknown as Plugin)
  .use(router)
  .use(pinia)
  .use(PiniaColada)
  .use(i18n)
  .use(Tres)
  .use(trackButtonPlugin)
  .mount('#app')

if (import.meta.env.DEV && !import.meta.env.SSR) {
  function captureEvents(el: HTMLElement) {
    // Force `pointer-events: auto` as DismissableLayer in Reka UI adds
    // `pointer-events: none` to document body.
    el.style.pointerEvents = 'auto'

    // We need to capture events inside elements like devtools to prevent them
    // from leaking to other layers (like DismissableLayer in Reka UI).
    //
    // See: https://github.com/unovue/reka-ui/blob/14866201d179b8bae3c8b4346a1ca8eff1c5eaa4/packages/radix-vue/src/DismissableLayer/DismissableLayer.vue#L186-L188
    el.addEventListener('focus', e => e.stopPropagation(), { capture: true })
    el.addEventListener('blur', e => e.stopPropagation(), { capture: true })
    el.addEventListener('pointerdown', e => e.stopPropagation(), { capture: true })
  }

  const observer = new MutationObserver((mutationsList, observer) => {
    for (const mutation of mutationsList) {
      if (mutation.type === 'childList') {
        const devtoolsContainer = document.getElementById('__vue-devtools-container__')

        if (devtoolsContainer) {
          captureEvents(devtoolsContainer)
          observer.disconnect()
        }
      }
    }
  })

  observer.observe(document.body, { childList: true, subtree: true })

  // Disconnect on timeout in case the MutationObserver is left here forever.
  // `observer.disconnect()` is idempotent, so it's safe to call it multiple times.
  setTimeout(() => observer.disconnect(), 15 * 1000)
}
